import { Card, CardContent } from '@/components/ui/card';
import { DollarSign, Receipt, Calendar } from 'lucide-react';
import { useCompanyLocale } from '@/hooks/useCompanyLocale';

interface ExpenseSummaryCardsProps {
  monthlyTotal: number;
  oneTimeTotal: number;
  periodLabel?: string | null;
  daysInMonth?: number;
}

export const ExpenseSummaryCards = ({
  monthlyTotal,
  oneTimeTotal,
  periodLabel,
  daysInMonth = 30,
}: ExpenseSummaryCardsProps) => {
  const { formatMoney: formatCurrency } = useCompanyLocale();
  const dailyAverage = monthlyTotal / daysInMonth;
  // monthlyTotal already includes one-time expenses (via calculatePeriodAmount)
  // so we display it directly — do NOT add oneTimeTotal again

  // Get short month name from periodLabel (e.g., "January 2026" -> "January")
  const shortMonth = periodLabel?.split(' ')[0] || 'Month';

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      {/* Month Total */}
      <Card className="bg-gradient-to-br from-destructive/10 to-destructive/5 border-destructive/20">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">{shortMonth} Total</p>
              <p className="text-2xl font-bold text-destructive mt-1">
                {formatCurrency(monthlyTotal)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Recurring + one-time
              </p>
            </div>
            <div className="p-2 bg-destructive/10 rounded-full">
              <DollarSign className="h-5 w-5 text-destructive" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* One-Time Costs */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">One-Time</p>
              <p className="text-2xl font-bold mt-1">
                {formatCurrency(oneTimeTotal)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Non-recurring costs
              </p>
            </div>
            <div className="p-2 bg-muted rounded-full">
              <Receipt className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Daily Average */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <p className="text-sm font-medium text-muted-foreground">Daily Avg</p>
              <p className="text-2xl font-bold mt-1">
                {formatCurrency(dailyAverage)}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Based on {daysInMonth} days
              </p>
            </div>
            <div className="p-2 bg-muted rounded-full">
              <Calendar className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};
